import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import Tree from 'react-d3-tree';

// Styled Components
const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const LeftPanel = styled.div`
  width: 350px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px;
  overflow-y: auto;
`;

const RightPanel = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 30px;
  font-size: 2.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ControlGroup = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Label = styled.label`
  display: block;
  color: white;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 5px;
  background: ${props => {
    if (props.$variant === 'danger') return '#ff6b6b';
    if (props.$variant === 'warning') return '#ffd43b';
    return '#4ecdc4';
  }};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 1000px;
  min-height: 700px;
  position: relative;
  overflow: hidden;
  
  /* Custom tree link styling */
  .tree-link {
    stroke: white !important;
    stroke-width: 2px !important;
    fill: none !important;
  }
  
  /* Remove default D3 tree background */
  .rd3t-tree-container {
    background: transparent !important;
  }
`;

const TrieInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
`;

const InfoText = styled.p`
  color: white;
  margin: 5px 0;
  font-size: 0.9rem;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatValue = styled.div`
  color: #4ecdc4;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: white;
  font-size: 0.8rem;
  opacity: 0.8;
`;

const OperationLog = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-height: 150px;
  overflow-y: auto;
`;

const LogTitle = styled.h3`
  color: white;
  margin-bottom: 10px;
  font-size: 1rem;
`;

const LogEntry = styled.div`
  color: white;
  font-size: 0.8rem;
  margin: 3px 0;
  padding: 5px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
`;

const SearchResult = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
`;

const SearchTitle = styled.h4`
  color: white;
  margin-bottom: 10px;
  font-size: 1rem;
`;

const SearchPath = styled.div`
  color: #4ecdc4;
  font-family: monospace;
  font-size: 1.1rem;
  background: rgba(0, 0, 0, 0.2);
  padding: 10px;
  border-radius: 5px;
  text-align: center;
`;

// Trie Node Class
class TrieNode {
  constructor() {
    this.children = new Map();
    this.isEndOfWord = false;
    this.word = '';
  }
}

// Trie Class
class Trie {
  constructor() {
    this.root = new TrieNode();
    this.size = 0;
  }

  // Deep copy method for proper trie cloning
  clone() {
    const newTrie = new Trie();
    newTrie.root = this.cloneNode(this.root);
    newTrie.size = this.size;
    return newTrie;
  }

  cloneNode(node) {
    if (!node) return null;
    
    const newNode = new TrieNode();
    newNode.isEndOfWord = node.isEndOfWord;
    newNode.word = node.word;
    
    for (let [char, childNode] of node.children) {
      newNode.children.set(char, this.cloneNode(childNode));
    }
    
    return newNode;
  }

  insert(word) {
    if (!word || word.trim() === '') return false;
    
    let current = this.root;
    const cleanWord = word.toLowerCase().trim();
    
    for (let char of cleanWord) {
      if (!current.children.has(char)) {
        current.children.set(char, new TrieNode());
      }
      current = current.children.get(char);
    }
    
    if (!current.isEndOfWord) {
      current.isEndOfWord = true;
      current.word = cleanWord;
      this.size++;
      return true;
    }
    return false;
  }

  search(word) {
    if (!word || word.trim() === '') return false;
    
    let current = this.root;
    const cleanWord = word.toLowerCase().trim();
    
    for (let char of cleanWord) {
      if (!current.children.has(char)) {
        return false;
      }
      current = current.children.get(char);
    }
    
    return current.isEndOfWord;
  }

  startsWith(prefix) {
    if (!prefix || prefix.trim() === '') return [];
    
    let current = this.root;
    const cleanPrefix = prefix.toLowerCase().trim();
    
    for (let char of cleanPrefix) {
      if (!current.children.has(char)) {
        return [];
      }
      current = current.children.get(char);
    }
    
    return this.getAllWordsFromNode(current, cleanPrefix);
  }

  getAllWordsFromNode(node, prefix) {
    const words = [];
    
    if (node.isEndOfWord) {
      words.push(prefix);
    }
    
    for (let [char, childNode] of node.children) {
      words.push(...this.getAllWordsFromNode(childNode, prefix + char));
    }
    
    return words;
  }

  delete(word) {
    if (!word || word.trim() === '') return false;
    
    const cleanWord = word.toLowerCase().trim();
    return this.deleteHelper(this.root, cleanWord, 0);
  }

  deleteHelper(node, word, index) {
    if (!node) return false;
    
    if (index === word.length) {
      if (node.isEndOfWord) {
        node.isEndOfWord = false;
        node.word = '';
        this.size--;
        return true;
      }
      return false;
    }
    
    const char = word[index];
    if (!node.children.has(char)) return false;
    
    const shouldDeleteChild = this.deleteHelper(node.children.get(char), word, index + 1);
    
    if (shouldDeleteChild && node.children.get(char).children.size === 0 && !node.children.get(char).isEndOfWord) {
      node.children.delete(char);
    }
    
    return shouldDeleteChild;
  }

  getAllWords() {
    return this.getAllWordsFromNode(this.root, '');
  }

  getHeight() {
    return this.getNodeHeight(this.root);
  }

  getNodeHeight(node) {
    if (!node || node.children.size === 0) return 0;
    
    let maxHeight = 0;
    for (let childNode of node.children.values()) {
      maxHeight = Math.max(maxHeight, this.getNodeHeight(childNode));
    }
    
    return maxHeight + 1;
  }
}

function TriePage() {
  const [trie, setTrie] = useState(new Trie());
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [prefixValue, setPrefixValue] = useState('');
  const [highlightedNode, setHighlightedNode] = useState(null);
  const [visitedNodes, setVisitedNodes] = useState(new Set());
  const [currentNode, setCurrentNode] = useState(null);
  const [operationLog, setOperationLog] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [prefixResult, setPrefixResult] = useState(null);
  const [animationSpeed, setAnimationSpeed] = useState(1000);
  const [isOperating, setIsOperating] = useState(false);

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const addToLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setOperationLog(prev => [...prev.slice(-9), `${timestamp}: ${message}`]);
  };

  const insertWord = async () => {
    try {
      if (!inputValue.trim()) return;
      
      const word = inputValue.trim();
      console.log('Attempting to insert word:', word);
      
      if (trie.search(word)) {
        addToLog(`Word "${word}" already exists in the trie`);
        return;
      }
      
      setIsOperating(true);
      setCurrentNode(word);
      addToLog(`Inserting "${word}" into the trie`);
      
      await delay(animationSpeed);
      
      console.log('Cloning trie...');
      const newTrie = trie.clone();
      console.log('Trie cloned successfully, size:', newTrie.size);
      
      console.log('Inserting word into cloned trie...');
      const insertResult = newTrie.insert(word);
      console.log('Insert result:', insertResult, 'New size:', newTrie.size);
      
      setTrie(newTrie);
      setCurrentNode(null);
      setIsOperating(false);
      addToLog(`Successfully inserted "${word}"`);
      setInputValue('');
      
      console.log('Word insertion completed successfully');
    } catch (error) {
      console.error('Error inserting word:', error);
      addToLog(`Error inserting word: ${error.message}`);
      setIsOperating(false);
      setCurrentNode(null);
    }
  };

  const deleteWord = async () => {
    try {
      if (!inputValue.trim()) return;
      
      const word = inputValue.trim();
      if (!trie.search(word)) {
        addToLog(`Word "${word}" not found in the trie`);
        return;
      }
      
      setIsOperating(true);
      setCurrentNode(word);
      addToLog(`Deleting "${word}" from the trie`);
      
      await delay(animationSpeed);
      
      const newTrie = trie.clone();
      newTrie.delete(word);
      
      setTrie(newTrie);
      setCurrentNode(null);
      setIsOperating(false);
      addToLog(`Successfully deleted "${word}"`);
      setInputValue('');
    } catch (error) {
      console.error('Error deleting word:', error);
      addToLog(`Error deleting word: ${error.message}`);
      setIsOperating(false);
      setCurrentNode(null);
    }
  };

  const searchWord = async () => {
    if (!searchValue.trim()) return;
    
    const word = searchValue.trim();
    
    setIsOperating(true);
    setCurrentNode(word);
    addToLog(`Searching for "${word}"`);
    
    await delay(animationSpeed);
    
    const found = trie.search(word);
    setCurrentNode(null);
    setIsOperating(false);
    
    if (found) {
      addToLog(`Found "${word}" in the trie`);
      setHighlightedNode(word);
      setSearchResult({ word, found: true });
      setTimeout(() => setHighlightedNode(null), 2000);
    } else {
      addToLog(`"${word}" not found in the trie`);
      setSearchResult({ word, found: false });
    }
  };

  const searchPrefix = async () => {
    if (!prefixValue.trim()) return;
    
    const prefix = prefixValue.trim();
    
    setIsOperating(true);
    setCurrentNode(prefix);
    addToLog(`Searching for words with prefix "${prefix}"`);
    
    await delay(animationSpeed);
    
    const words = trie.startsWith(prefix);
    setCurrentNode(null);
    setIsOperating(false);
    
    if (words.length > 0) {
      setPrefixResult({ prefix, words });
      addToLog(`Found ${words.length} words with prefix "${prefix}"`);
    } else {
      setPrefixResult({ prefix, words: [] });
      addToLog(`No words found with prefix "${prefix}"`);
    }
  };

  const showAllWords = async () => {
    if (trie.size === 0) {
      addToLog('Trie is empty - no words to display');
      return;
    }
    
    setIsOperating(true);
    addToLog('Displaying all words in trie');
    
    const words = trie.getAllWords();
    setSearchResult({ type: 'all_words', words });
    setIsOperating(false);
    addToLog(`Displayed ${words.length} words from trie`);
  };

  const clearTrie = () => {
    if (trie.size === 0) {
      addToLog('Trie is already empty');
      return;
    }
    
    setTrie(new Trie());
    setVisitedNodes(new Set());
    setCurrentNode(null);
    setHighlightedNode(null);
    setSearchResult(null);
    setPrefixResult(null);
    addToLog('Trie cleared');
  };

  const generateRandomTrie = () => {
    const words = ['hello', 'world', 'algorithm', 'data', 'structure', 'computer', 'science', 'programming', 'visualization', 'tree', 'trie', 'graph', 'search', 'sort', 'binary', 'recursion', 'dynamic', 'backtracking', 'hash', 'heap', 'stack', 'queue'];
    const newTrie = new Trie();
    const numWords = Math.floor(Math.random() * 10) + 5; // 5-14 words
    
    for (let i = 0; i < numWords; i++) {
      const randomWord = words[Math.floor(Math.random() * words.length)];
      if (!newTrie.search(randomWord)) {
        newTrie.insert(randomWord);
      }
    }
    
    setTrie(newTrie);
    setVisitedNodes(new Set());
    setCurrentNode(null);
    setHighlightedNode(null);
    setSearchResult(null);
    setPrefixResult(null);
    addToLog(`Generated random trie with ${newTrie.size} words`);
  };

  // Convert trie to D3 tree format
  const convertTrieToD3Format = useCallback((node, char = '') => {
    if (!node) return null;
    
    const d3Node = {
      name: char || 'ROOT',
      attributes: {
        char: char,
        isEndOfWord: node.isEndOfWord,
        word: node.word,
        isHighlighted: highlightedNode === node.word,
        isVisited: visitedNodes.has(node.word),
        isCurrent: currentNode === node.word,
      },
      children: []
    };
    
    for (let [childChar, childNode] of node.children) {
      const childD3Node = convertTrieToD3Format(childNode, childChar);
      if (childD3Node) {
        d3Node.children.push(childD3Node);
      }
    }
    
    return d3Node;
  }, [highlightedNode, visitedNodes, currentNode]);

  // Get D3 tree data
  const trieData = trie.root ? convertTrieToD3Format(trie.root) : null;

  // Custom node styling
  const renderCustomNode = ({ nodeDatum, toggleNode }) => {
    const isHighlighted = nodeDatum.attributes?.isHighlighted;
    const isVisited = nodeDatum.attributes?.isVisited;
    const isCurrent = nodeDatum.attributes?.isCurrent;
    const isEndOfWord = nodeDatum.attributes?.isEndOfWord;
    const char = nodeDatum.attributes?.char;
    
    let backgroundColor = '#ffffff';
    let borderColor = '#4ecdc4';
    
    if (isHighlighted) backgroundColor = '#ffd43b';
    else if (isVisited) backgroundColor = '#4ecdc4';
    else if (isCurrent) backgroundColor = '#51cf66';
    
    if (isEndOfWord) borderColor = '#ff6b6b';
    
    const nodeSize = char === 'ROOT' ? 80 : 60;
    
    return (
      <g>
        <circle
          r={nodeSize}
          fill={backgroundColor}
          stroke={borderColor}
          strokeWidth={isEndOfWord ? '8' : '5'}
          style={{ cursor: 'pointer' }}
          onClick={toggleNode}
        />
        <text
          textAnchor="middle"
          dy=".35em"
          style={{ 
            fontSize: char === 'ROOT' ? '28px' : '32px', 
            fontWeight: '900', 
            fill: '#000',
            textShadow: '0 0 4px rgba(255, 255, 255, 1)',
            fontFamily: 'Arial, sans-serif',
            letterSpacing: '0.5px'
          }}
        >
          {char === 'ROOT' ? 'ROOT' : char}
        </text>
        {isEndOfWord && (
          <text
            textAnchor="middle"
            dy="3em"
            style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              fill: '#ff6b6b',
              fontFamily: 'Arial, sans-serif'
            }}
          >
            {nodeDatum.attributes.word}
          </text>
        )}
      </g>
    );
  };

  return (
    <PageContainer>
      <LeftPanel>
        <Title>Trie Visualizer</Title>
        
        <Controls>
          {/* Trie Operations */}
          <ControlGroup>
            <Label>Trie Operations</Label>
            <Input
              type="text"
              placeholder="Enter word to insert/delete"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && insertWord()}
            />
            <Button onClick={insertWord} disabled={isOperating}>Insert Word</Button>
            <Button onClick={deleteWord} disabled={isOperating} $variant="warning">Delete Word</Button>
            <Button onClick={generateRandomTrie} disabled={isOperating}>Generate Random Trie</Button>
            <Button onClick={clearTrie} disabled={isOperating} $variant="danger">Clear Trie</Button>
          </ControlGroup>
          
          {/* Search Operations */}
          <ControlGroup>
            <Label>Search Operations</Label>
            <Input
              type="text"
              placeholder="Enter word to search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchWord()}
            />
            <Button onClick={searchWord} disabled={isOperating}>Search Word</Button>
            
            <Input
              type="text"
              placeholder="Enter prefix to search"
              value={prefixValue}
              onChange={(e) => setPrefixValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchPrefix()}
            />
            <Button onClick={searchPrefix} disabled={isOperating}>Search Prefix</Button>
          </ControlGroup>
          
          {/* Trie Operations */}
          <ControlGroup>
            <Label>Trie Operations</Label>
            <Button onClick={showAllWords} disabled={isOperating}>Show All Words</Button>
          </ControlGroup>
          
          {/* Animation Speed Control */}
          <ControlGroup>
            <Label>Animation Speed: {animationSpeed}ms</Label>
            <Input
              type="range"
              min="500"
              max="3000"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseInt(e.target.value))}
            />
          </ControlGroup>
          
          {/* Stats */}
          <StatsContainer>
            <StatBox>
              <StatValue>{trie.size}</StatValue>
              <StatLabel>Words</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{trie.getHeight()}</StatValue>
              <StatLabel>Height</StatLabel>
            </StatBox>
          </StatsContainer>
          
          {/* Operation Log */}
          <OperationLog>
            <LogTitle>Operation Log</LogTitle>
            {operationLog.map((entry, index) => (
              <LogEntry key={index}>{entry}</LogEntry>
            ))}
          </OperationLog>
        </Controls>
      </LeftPanel>
      
      <RightPanel>
        <VisualizationContainer>
          {trieData ? (
            <div style={{ width: '100%', height: '650px', display: 'flex', justifyContent: 'center' }}>
              <Tree
                data={trieData}
                renderCustomNodeElement={renderCustomNode}
                orientation="vertical"
                pathFunc="step"
                separation={{ siblings: 2.2, nonSiblings: 2.8 }}
                translate={{ x: 500, y: 100 }}
                nodeSize={{ x: 180, y: 160 }}
                zoom={0.75}
                enableLegends={false}
                pathClassFunc={() => 'tree-link'}
                style={{
                  links: {
                    stroke: 'white',
                    strokeWidth: 3,
                    fill: 'none'
                  }
                }}
              />
            </div>
          ) : (
            <div style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontStyle: 'italic', 
              fontSize: '1.2rem',
              textAlign: 'center',
              padding: '50px'
            }}>
              Trie is empty
            </div>
          )}
          
          {/* Trie Info */}
          <TrieInfo>
            <InfoText><strong>Trie:</strong> A tree-like data structure used to store and retrieve strings</InfoText>
            <InfoText><strong>Operations:</strong> Insert, delete, search words, and find words with common prefixes</InfoText>
            <InfoText><strong>Features:</strong> Efficient prefix searching and string operations</InfoText>
          </TrieInfo>
          
          {/* Search Result */}
          {searchResult && (
            <SearchResult>
              <SearchTitle>
                {searchResult.type === 'all_words' 
                  ? 'All Words in Trie:'
                  : `Search Result for "${searchResult.word}":`
                }
              </SearchTitle>
              <SearchPath>
                {searchResult.type === 'all_words' 
                  ? searchResult.words.join(' → ')
                  : searchResult.found ? 'Found' : 'Not Found'
                }
              </SearchPath>
            </SearchResult>
          )}
          
          {/* Prefix Search Result */}
          {prefixResult !== null && (
            <SearchResult>
              <SearchTitle>Prefix Search Results for "{prefixResult.prefix}":</SearchTitle>
              <SearchPath>
                {prefixResult.words.length > 0 ? prefixResult.words.join(' → ') : 'No words found'}
              </SearchPath>
            </SearchResult>
          )}
        </VisualizationContainer>
      </RightPanel>
    </PageContainer>
  );
}

export default TriePage;
